import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, StatusBadge } from './ui'
import type { OrganizationMember } from '../api/organizations'

interface MemberTableProps {
  members: OrganizationMember[]
  isOwner: boolean
  isOwnerOrAdmin: boolean
  onRemove: (memberId: string) => void
  onUpdateRole: (memberId: string, newRole: string) => void
}

export const MemberTable: React.FC<MemberTableProps> = ({
  members,
  isOwner,
  isOwnerOrAdmin,
  onRemove,
  onUpdateRole,
}) => {
  const intl = useIntl()
  if (members.length === 0) {
    return (
      <div className="text-center py-8 text-foreground-muted">
        <FormattedMessage id="members.empty" />
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="members.col.name" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="members.col.email" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="members.col.role" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="members.col.joined" /></th>
            {isOwnerOrAdmin && (
              <th className="pb-3 font-semibold text-foreground text-right"><FormattedMessage id="members.col.actions" /></th>
            )}
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.id} className="border-b border-border last:border-0">
              <td className="py-3 font-medium text-foreground">
                {member.user.full_name || '—'}
              </td>
              <td className="py-3 text-foreground-muted">{member.user.email}</td>
              <td className="py-3">
                {isOwner && member.role !== 'OWNER' ? (
                  <select
                    value={member.role}
                    onChange={(e) => onUpdateRole(member.id, e.target.value)}
                    className="text-sm px-2 py-1 rounded border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="RESPONDENT">{intl.formatMessage({ id: 'members.role.RESPONDENT' })}</option>
                    <option value="ADMIN">{intl.formatMessage({ id: 'members.role.ADMIN' })}</option>
                  </select>
                ) : (
                  <StatusBadge status={member.role} />
                )}
              </td>
              <td className="py-3 text-foreground-muted">
                {new Date(member.joined_at).toLocaleDateString()}
              </td>
              {isOwnerOrAdmin && (
                <td className="py-3 text-right">
                  {member.role !== 'OWNER' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemove(member.id)}
                      className="text-error hover:text-error"
                    >
                      <FormattedMessage id="members.remove" />
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
